import type {
  OpenWidgetOptions,
  WidgetConfig,
  WidgetHandle,
} from '@/src/widget/sdk/types';

type WidgetRuntimeConfig = WidgetConfig & {
  apiBaseUrl: string;
  authApiBaseUrl: string;
  wsUrl: string;
  tenantApiBaseUrl: string;
};

declare global {
  interface Window {
    PENTESITES_CONFIG?: WidgetConfig;
    PenteSitesWidgetBundle?: {
      initializeWidget: (config: WidgetRuntimeConfig) => WidgetHandle;
    };
    PenteSitesWidget?: {
      openWidget(options?: OpenWidgetOptions): void;
      closeWidget(): void;
    };
  }
}

const BUILD_TIME_API_BASE_URL = import.meta.env.VITE_WIDGET_API_BASE_URL;
const BUILD_TIME_API_AUTH = import.meta.env.VITE_WIDGET_API_AUTH;
const BUILD_TIME_WS_URL = import.meta.env.VITE_WIDGET_WS_URL;
const BUILD_TIME_TENANT_URL = import.meta.env.VITE_WIDGET_TENANT_URL;
const BUILD_TIME_ADMIN_PANEL_URL = import.meta.env.VITE_WIDGET_ADMIN_PANEL;
const BUNDLE_SCRIPT_ID = 'pentesites-widget-bundle-script';
const BUNDLE_READY_TIMEOUT_MS = 3000;
const BUNDLE_READY_POLL_MS = 50;

function getCurrentScript(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }

  const scripts = document.getElementsByTagName('script');
  return scripts.length ? scripts[scripts.length - 1] : null;
}

function getBundleUrl() {
  const currentScript = getCurrentScript();
  const currentScriptUrl = currentScript?.src;

  if (!currentScriptUrl) {
    throw new Error('Unable to determine the widget loader script URL.');
  }

  return new URL('widget.bundle.js', currentScriptUrl).toString();
}

function getRuntimeConfig(): WidgetRuntimeConfig {
  if (
    !BUILD_TIME_API_BASE_URL ||
    !BUILD_TIME_API_AUTH ||
    !BUILD_TIME_WS_URL ||
    !BUILD_TIME_TENANT_URL
  ) {
    throw new Error(
      'VITE_WIDGET_API_BASE_URL, VITE_WIDGET_API_AUTH, VITE_WIDGET_WS_URL, and VITE_WIDGET_TENANT_URL must be defined at build time to initialize the widget.',
    );
  }

  return {
    ...(window.PENTESITES_CONFIG ?? {}),
    apiBaseUrl: BUILD_TIME_API_BASE_URL,
    authApiBaseUrl: BUILD_TIME_API_AUTH,
    wsUrl: BUILD_TIME_WS_URL,
    tenantApiBaseUrl: BUILD_TIME_TENANT_URL,
    adminPanelUrl: BUILD_TIME_ADMIN_PANEL_URL,
  };
}

function waitForWidgetRuntime() {
  const startedAt = Date.now();

  return new Promise<NonNullable<Window['PenteSitesWidgetBundle']>>(
    (resolve, reject) => {
      const checkRuntime = () => {
        const runtime = window.PenteSitesWidgetBundle;

        if (runtime?.initializeWidget) {
          resolve(runtime);
          return;
        }

        if (Date.now() - startedAt >= BUNDLE_READY_TIMEOUT_MS) {
          reject(
            new Error(
              'Widget bundle loaded but initializeWidget is unavailable.',
            ),
          );
          return;
        }

        window.setTimeout(checkRuntime, BUNDLE_READY_POLL_MS);
      };

      checkRuntime();
    },
  );
}

async function initializeWhenReady() {
  const runtime = await waitForWidgetRuntime();
  const handle = runtime.initializeWidget(getRuntimeConfig());
  window.PenteSitesWidget = {
    openWidget: options => handle.openWidget(options),
    closeWidget: () => handle.closeWidget(),
  };
}

function loadBundleScript() {
  const existing = document.getElementById(
    BUNDLE_SCRIPT_ID,
  ) as HTMLScriptElement | null;

  if (existing) {
    if (window.PenteSitesWidgetBundle?.initializeWidget) {
      initializeWhenReady();
      return;
    }

    existing.addEventListener(
      'load',
      () => {
        void initializeWhenReady();
      },
      { once: true },
    );
    return;
  }

  const script = document.createElement('script');
  script.id = BUNDLE_SCRIPT_ID;
  script.src = getBundleUrl();
  script.async = true;
  script.onload = () => {
    void initializeWhenReady();
  };
  script.onerror = () => {
    throw new Error('Failed to load widget.bundle.js');
  };
  document.head.appendChild(script);
}

loadBundleScript();
